<template lang="html">
	<div class="omapi-settings-page omapi-settings-general">
		<slot name="tabs"></slot>

		<common-alerts id="om-settings-errors" :alerts="alerts" />

		<common-card title="General">
			<div class="omapi-card-setting-section">
				<header>
					<h3>License</h3>
				</header>
				<div>
					<div v-if="connected && apiKeyValid" class="your-plan">
						You're using <b>{{ userPlanName }}</b> - no license needed. Enjoy! 🙂
					</div>

					<div class="row-connect-buttons">
						<template v-if="!connected">
							<core-wizard-button v-show="!connectLoading" size="large">
								Create a Free Account to Get Started
							</core-wizard-button>
							<span v-show="!connectLoading" class="row-connect-buttons-or"> or </span>
							<core-connect-button
								:output-errors="false"
								type="primary"
								size="large"
								:refresh-after="false"
								@onSuccess="afterConnect"
							>
								Connect an Existing Account
							</core-connect-button>
						</template>
						<template v-else>
							<core-alert
								v-show="!connectLoading && apiKeyValid"
								type="success"
								icon="success"
								class="omapi-settings-page__connected"
							>
								<div class="alert-message">Your account is connected!</div>
							</core-alert>
							<core-connect-button
								:output-errors="false"
								type="primary"
								size="medium"
								:disconnect="true"
								class="omapi-settings-page__disconnect"
							>
								<svg-close /> Disconnect
							</core-connect-button>
						</template>
					</div>

					<div v-if="showProUpsells" class="omapi-action-box">
						<header>
							<div>
								To unlock more features consider
								<core-upgrade-button
									utm-medium="GeneralSettings"
									class="omapi-link-green omapi-link-underline"
									:link-style="true"
									>upgrading to PRO.
								</core-upgrade-button>
							</div>
						</header>
						<div>
							As a valued {{ userPlanName }} user, you receive
							<span class="text-black font-bold">35% off</span> automatically applied at checkout!
						</div>
					</div>

					<div
						v-if="connected && apiKeyValid"
						class="omapi-multi-column omapi-flex omapi-flex-row omapi-user-info"
					>
						<img
							:src="gravatar"
							:alt="userFullName"
							class="omapi-multi-column__secondary omapi-icon-wrapper__left gravatar-image"
						/>
						<div class="omapi-multi-column__primary">
							<h3 class="omapi-multi-column__title mt-6">{{ userFullName }}</h3>
							<p v-if="!!company" class="omapi-multi-column__text mb-0">{{ company }}</p>
							<p class="omapi-multi-column__text">{{ userEmail }}</p>
						</div>
					</div>
				</div>
			</div>

			<settings-connect-woocommerce v-if="isWooActive" />

			<settings-connect-edd v-if="isEddActive" />
		</common-card>

		<settings-pro-upsell v-if="showProUpsells" />
	</div>
</template>

<script lang="js">
import { mapState, mapGetters } from 'vuex'
import md5 from 'md5';

export default {
	computed : {
		...mapState([ 'alerts', 'apiKeyValid' ]),
		...mapGetters([ 'connected', 'showProUpsells', 'userPlanName', 'userFullName', 'userEmail', 'company' ]),
		...mapGetters('wp', [ 'isWooActive', 'isEddActive' ]),
		connectLoading () {
			return this.$store.getters.isLoading([ 'connect', 'disconnect' ])
		},
		gravatar () {
			const userEmailHash = md5(this.userEmail)
			return `https://www.gravatar.com/avatar/${userEmailHash}?s=200&d=mp`
		},
	},
	methods : {
		afterConnect (disconnected) {
			this.$store.commit('loading', disconnected ? 'disconnect' : 'connect')
			this.$store.commit('clearAlerts')
			this.$store.commit('addAlert', {
				message : disconnected
					? 'Site successfully disconnected. Reloading, please wait...'
					: 'Site connected successfully! Reloading, please wait...',
				type : 'success',
			})
			setTimeout(() => window.location.reload(), 1500)
		},
	},
};
</script>
<style lang="scss">
@import '../../assets/scss/variables';
.omapi-screen .omapi-settings-general {
	.omapi-button:not(.loading-button) {
		&[disabled] {
			display: flex;
			justify-content: center;
			align-items: center;
			flex-direction: column;
			opacity: 1;
			> span {
				color: transparent;
				height: 0;
			}
		}
	}
}
.omapi-screen .omapi-settings-page {
	.omapi-button {
		.om-circle-loading {
			margin: 0;
		}
	}
	.omapi-settings-page__plugin-connected.omapi-alert {
		padding: 6px 20px 5px;
		display: inline-flex;

		.alert-message {
			font-weight: $font_bold;
		}
	}
	.omapi-has-tooltip .popper {
		max-width: 1000px;
	}

	&__disconnect.omapi-button {
		display: inline-flex;
		justify-content: center;
		align-items: center;
		svg {
			width: 10px;
			height: 10px;
			margin-right: 3px;
		}
	}
}
.omapi-action-box {
	background-color: $color_white-button-background;
	color: $font_setting-color;
	padding: 20px;
	header {
		color: $color_black;
		font-size: 16px;
		font-weight: 600;
		margin-bottom: 10px;
	}
}
.omapi-user-info {
	margin-top: 20px;
}
.your-plan {
	margin: 0 0 20px;
}
.row-connect-buttons {
	align-content: center;
	align-items: center;
	display: flex;
	margin: 0 0 20px;
	@media screen and (max-width: 920px) {
		flex-wrap: wrap;
		align-items: flex-start;
		margin-bottom: 0;
	}

	.omapi-button {
		margin: 0 !important;
		@media screen and (max-width: 920px) {
			margin-bottom: 10px !important;
		}
	}

	.omapi-settings-page__connected.omapi-alert {
		padding: 6px 20px 5px;
		margin-right: 18px;
		margin-bottom: 0;
		@media screen and (max-width: 920px) {
			margin-bottom: 10px;
		}
		.alert-message {
			font-weight: $font_bold;
		}
	}

	.omapi-button.omapi-button__medium {
		font-size: 14px;
	}

	&-or {
		align-self: center;
		margin: 0 10px;
	}
}

.gravatar-image {
	width: 80px;
	height: 80px;
}

.mt-6 {
	margin-top: 6px !important;
}

.mb-0 {
	margin-bottom: 0 !important;
}
</style>
